import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import api from '../lib/api';
import {
  ClipboardList, Mail, Plus, X, Send, Save, Clock, Users,
  CheckCircle2, AlertCircle, History,
} from 'lucide-react';
import { toast } from 'sonner';

/** Lista editable de chips de email — agregar/quitar. */
function EmailChips({ label, items, onChange, placeholder, testId = 'emails' }) {
  const [draft, setDraft] = useState('');
  const add = () => {
    const v = draft.trim().toLowerCase();
    if (!v || items.includes(v)) { setDraft(''); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
      toast.error('Email inválido');
      return;
    }
    onChange([...items, v]);
    setDraft('');
  };
  const remove = (e) => onChange(items.filter(x => x !== e));
  return (
    <div>
      <label className="block text-xs font-medium mb-1.5 flex items-center gap-1">
        <Users className="w-3.5 h-3.5" /> {label}
      </label>
      <div className="flex gap-1 mb-1.5">
        <Input
          type="email"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder={placeholder}
          className="text-sm"
          data-testid={`${testId}-input`}
        />
        <Button size="sm" type="button" onClick={add} variant="outline" data-testid={`${testId}-add`}>
          <Plus className="w-4 h-4" />
        </Button>
      </div>
      <div className="flex flex-wrap gap-1.5 min-h-[28px]">
        {items.length === 0 && <span className="text-xs text-muted-foreground italic">Sin destinatarios.</span>}
        {items.map(e => (
          <Badge key={e} variant="secondary" className="gap-1 pr-1" data-testid={`${testId}-chip`}>
            {e}
            <button type="button" onClick={() => remove(e)} className="hover:bg-red-200 rounded-full p-0.5">
              <X className="w-3 h-3" />
            </button>
          </Badge>
        ))}
      </div>
    </div>
  );
}

/** Iter 308.b (Renzo) — Recordatorio de Requisiciones pendientes de aprobación.
 *
 * Lógica del envío (backend):
 *   • Alerta individual: se envía UNA sola vez por requisición al cumplir
 *     `dias_umbral` días (default 3) sin aprobación de Gerencia.
 *   • Resumen semanal: cada PRIMER día hábil de la semana (Lun / Mar si Lun
 *     es feriado / etc.) a la hora configurada se envía un resumen con TODAS
 *     las pendientes.
 *   • Cron Lun-Vie a la hora configurada, timezone America/Santiago.
 */
export default function RequisicionesPendientesSection() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(null);
  const [history, setHistory] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, p, h] = await Promise.all([
        api.get('/recordatorios/requisiciones-pendientes/config'),
        api.get('/recordatorios/requisiciones-pendientes/preview'),
        api.get('/recordatorios/requisiciones-pendientes/log?limit=10'),
      ]);
      setCfg(c.data);
      setPreview(p.data);
      setHistory(h.data?.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error cargando recordatorio requisiciones');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put('/recordatorios/requisiciones-pendientes/config', cfg);
      setCfg(res.data);
      toast.success('Configuración guardada');
      api.get('/recordatorios/requisiciones-pendientes/preview').then(r => setPreview(r.data));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error guardando');
    } finally {
      setSaving(false);
    }
  };

  const handleSendNow = async () => {
    if (!cfg.to || cfg.to.length === 0) {
      toast.error('Agrega al menos un destinatario antes de enviar');
      return;
    }
    setSending(true);
    try {
      const res = await api.post('/recordatorios/requisiciones-pendientes/send-now');
      const d = res.data;
      if (d.sent) {
        toast.success(`Email enviado (${d.count} requisición(es))`);
      } else {
        toast.info(`No enviado: ${d.reason || d.error || 'sin items'}`);
      }
      api.get('/recordatorios/requisiciones-pendientes/log?limit=10').then(r => setHistory(r.data?.items || []));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error enviando');
    } finally {
      setSending(false);
    }
  };

  if (loading || !cfg) {
    return <Skeleton className="h-48 w-full" />;
  }

  return (
    <Card data-testid="card-recordatorio-requisiciones">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-orange-600" /> Requisiciones pendientes de aprobación
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Alerta a Gerencia cuando una requisición lleva más de N días sin aprobar.
              Se envía una sola vez al cumplirse el plazo por cada requisición, y un resumen
              consolidado el primer día hábil de cada semana.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">{cfg.enabled ? 'Activo' : 'Pausado'}</span>
            <Switch
              checked={cfg.enabled}
              onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })}
              data-testid="switch-req-enabled"
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium mb-1.5 flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" /> Umbral de días
            </label>
            <Input
              type="number" min={1} max={30}
              value={cfg.dias_umbral}
              onChange={(e) => setCfg({ ...cfg, dias_umbral: parseInt(e.target.value, 10) || 3 })}
              data-testid="input-req-dias-umbral"
            />
            <p className="text-[11px] text-muted-foreground mt-0.5">
              Requisiciones sin aprobación de Gerencia hace ≥ X días.
            </p>
          </div>
          <div>
            <label className="block text-xs font-medium mb-1.5">Hora de envío (Chile)</label>
            <Input
              type="time"
              value={cfg.hora}
              onChange={(e) => setCfg({ ...cfg, hora: e.target.value })}
              data-testid="input-req-hora"
            />
            <p className="text-[11px] text-muted-foreground mt-0.5">
              Lun-Vie. El cron decide cada día si toca alerta individual o resumen semanal.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t">
          <EmailChips
            label="Destinatarios principales (Para)"
            items={cfg.to}
            onChange={(t) => setCfg({ ...cfg, to: t })}
            placeholder="c.chirino@chirinosteel.com"
            testId="req-to"
          />
          <EmailChips
            label="Copia (CC)"
            items={cfg.cc}
            onChange={(c) => setCfg({ ...cfg, cc: c })}
            placeholder="copia@chirinosteel.com"
            testId="req-cc"
          />
        </div>

        <div className="flex flex-wrap gap-2 pt-3 border-t">
          <Button
            onClick={handleSave} disabled={saving}
            className="bg-orange-600 hover:bg-orange-700"
            data-testid="btn-req-save"
          >
            <Save className="w-4 h-4 mr-1.5" /> {saving ? 'Guardando…' : 'Guardar'}
          </Button>
          <Button
            onClick={handleSendNow} disabled={sending || !cfg.to.length}
            variant="outline"
            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            data-testid="btn-req-send-now"
          >
            <Send className="w-4 h-4 mr-1.5" /> {sending ? 'Enviando…' : 'Enviar ahora (test)'}
          </Button>
        </div>

        {/* Preview */}
        <div className="border rounded-md">
          <div className="px-3 py-2 bg-muted/40 text-xs font-medium flex items-center justify-between">
            <span>Vista previa: {preview?.count || 0} requisición(es) pendientes (≥ {preview?.dias_umbral || cfg.dias_umbral} días)</span>
          </div>
          <div className="max-h-56 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-muted/30 sticky top-0">
                <tr>
                  <th className="text-left px-3 py-1.5">N° Req.</th>
                  <th className="text-left px-3 py-1.5">Fecha</th>
                  <th className="text-left px-3 py-1.5">Solicitante</th>
                  <th className="text-center px-3 py-1.5">OT</th>
                  <th className="text-center px-3 py-1.5">Días</th>
                  <th className="text-center px-3 py-1.5">Alerta enviada</th>
                </tr>
              </thead>
              <tbody>
                {(preview?.items || []).map((i, idx) => (
                  <tr key={idx} className="border-t hover:bg-muted/30">
                    <td className="px-3 py-1.5 font-mono font-semibold">#{i.numero}</td>
                    <td className="px-3 py-1.5 text-muted-foreground">{i.fecha_creacion_fmt}</td>
                    <td className="px-3 py-1.5 truncate max-w-xs">{i.solicitante || '—'}</td>
                    <td className="px-3 py-1.5 text-center">{i.ot_numero || '—'}</td>
                    <td className={`px-3 py-1.5 text-center font-bold ${i.dias >= 7 ? 'text-red-600' : 'text-amber-700'}`}>{i.dias}d</td>
                    <td className="px-3 py-1.5 text-center">
                      {i.alerta_enviada
                        ? <CheckCircle2 className="w-3.5 h-3.5 inline text-emerald-600" />
                        : <span className="text-muted-foreground">—</span>}
                    </td>
                  </tr>
                ))}
                {(preview?.items?.length || 0) === 0 && (
                  <tr><td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">
                    No hay requisiciones pendientes. 🎉
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Historial */}
        {history.length > 0 && (
          <div className="border rounded-md">
            <div className="px-3 py-2 bg-muted/40 text-xs font-medium flex items-center gap-1">
              <History className="w-3.5 h-3.5" /> Últimos envíos
            </div>
            <div className="divide-y">
              {history.slice(0, 5).map((h, idx) => (
                <div key={idx} className="px-3 py-1.5 flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    {h.success ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> : <AlertCircle className="w-3.5 h-3.5 text-red-600" />}
                    <span>{new Date(h.timestamp).toLocaleString('es-CL')}</span>
                    <Badge variant="outline" className="text-[10px]">{h.manual ? 'manual' : (h.modo || 'auto')}</Badge>
                  </div>
                  <div className="text-muted-foreground">
                    {h.items_count} req.
                    {h.error && <span className="text-red-600 ml-2">{h.error}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
